/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useEffect, useState } from 'react';
import { useFirestoreBalance } from '../../hooks/use-firestore-balance';
import { convertMoneyToCredits } from '../../lib/services/razorpay-service';
import './BalanceWarning.css';

interface BalanceWarningProps {
  onDismiss?: () => void;
}

export const BalanceWarning: React.FC<BalanceWarningProps> = ({ onDismiss }) => {
  const { balance, loading } = useFirestoreBalance();
  const [showLowBalance, setShowLowBalance] = useState(false);
  const [showOutOfBalance, setShowOutOfBalance] = useState(false);
  const [hasShownLowBalance, setHasShownLowBalance] = useState(false);

  useEffect(() => {
    if (loading || balance === null) return;

    // Check for out of balance (0 or negative)
    if (balance <= 0) {
      setShowOutOfBalance(true);
      setShowLowBalance(false);
      return;
    }

    // Check for low balance (<= ₹100) - show only once per session
    if (balance <= 100 && !hasShownLowBalance) {
      setShowLowBalance(true);
      setShowOutOfBalance(false);
      setHasShownLowBalance(true);
    } else if (balance > 100) {
      // Reset low balance flag if balance goes above ₹100
      setShowLowBalance(false);
      setHasShownLowBalance(false);
    }
  }, [balance, loading, hasShownLowBalance]);

  const handleRecharge = () => {
    // Navigate to Payment Gateway using custom event
    // The app listens for this event in App.tsx
    window.dispatchEvent(new CustomEvent('navigate', { 
      detail: { view: 'Payment Gateway' as any } 
    }));
    if (onDismiss) onDismiss();
  };

  const handleDismiss = () => {
    setShowLowBalance(false);
    setShowOutOfBalance(false);
    if (onDismiss) onDismiss();
  };

  if (loading) return null;

  // Out of Balance Warning (Priority)
  if (showOutOfBalance) {
    return (
      <div className="balance-warning-overlay">
        <div className="balance-warning-modal out-of-balance">
          <div className="balance-warning-icon">
            <span className="material-symbols-outlined">account_balance_wallet</span>
          </div>
          <h2 className="balance-warning-title">Out of Credits</h2>
          <p className="balance-warning-message">
            Your account has {balance !== null ? convertMoneyToCredits(balance).toLocaleString('en-IN') : '0'} credits. 
            You need to buy credits to continue using AI conversations.
          </p>
          <div className="balance-warning-actions">
            <button 
              className="balance-warning-btn primary"
              onClick={handleRecharge}
            >
              Buy Credits
            </button>
            <button 
              className="balance-warning-btn secondary"
              onClick={handleDismiss}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Low Balance Warning
  if (showLowBalance) {
    return (
      <div className="balance-warning-overlay">
        <div className="balance-warning-modal low-balance">
          <div className="balance-warning-icon">
            <span className="material-symbols-outlined">warning</span>
          </div>
          <h2 className="balance-warning-title">Low Credits Alert</h2>
          <p className="balance-warning-message">
            Your account has {balance !== null ? convertMoneyToCredits(balance).toLocaleString('en-IN') : '0'} credits (less than 1,000 credits). 
            Please buy more credits soon to avoid interruption in service.
          </p>
          <div className="balance-warning-actions">
            <button 
              className="balance-warning-btn primary"
              onClick={handleRecharge}
            >
              Buy Credits
            </button>
            <button 
              className="balance-warning-btn secondary"
              onClick={handleDismiss}
            >
              Remind Me Later
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

